import React, { useState } from "react";
import { Link } from "react-router-dom";
import { OpenPositions } from "../pages/CareersOpenPositions";
import careersCollaborationImage from "../../images/careers_delnova_collaboration.png";

export default function CareersRedesign() {
  const [activeTab, setActiveTab] = useState("All");
  const [expandedCard, setExpandedCard] = useState("");

  const departments = ["All", ...new Set(OpenPositions.map(job => job.department))];

  const handleExpandToggle = (cardId, e) => {
    // Prevent event bubbling if clicking the apply button or other children
    e.stopPropagation();
    if (expandedCard === cardId) {
      setExpandedCard("");
    } else {
      setExpandedCard(cardId);
    }
  };

  const filteredJobs = activeTab === "All"
    ? OpenPositions
    : OpenPositions.filter(job => job.department === activeTab);

  return (
    <div className="bg-[#F7F9FA] min-h-screen text-slate-800">
      {/* 1. Hero Header Section */}
      <section className="relative min-h-[40vh] flex items-center justify-center pt-32 pb-16 overflow-hidden bg-brand-blue-deep">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30 mix-blend-overlay"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1920&auto=format&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-blue-deep via-brand-blue/90 to-brand-blue/30" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center text-white z-10">
          <div className="inline-flex items-center space-x-2 bg-brand-teal/20 border border-brand-teal/30 rounded-full px-4 py-1.5 mb-4">
            <span className="text-brand-teal-light font-semibold text-xs uppercase tracking-wider">
              Work With Us
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 leading-tight">
            Careers at DelNova Healthcare
          </h1>
          <p className="max-w-2xl mx-auto text-base md:text-lg text-slate-300 font-light leading-relaxed">
            Join a people-oriented organization where formulation science meets innovation. We empower our teams to develop stable, scalable, and complex generic therapeutics.
          </p>
        </div>
      </section>

      {/* 2. Culture & Values Section */}
      <section className="py-20 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Text block */}
          <div className="lg:col-span-6 space-y-6">
            <div className="inline-block text-xs font-semibold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-3 py-1 rounded-full">
              Our Culture & Philosophy
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-brand-blue tracking-tight leading-tight">
              We are a People-Oriented Organization
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed text-base md:text-lg font-light">
              <p>
                At DelNova Healthcare, we believe that our greatest asset is “DelNovians”. We empower our employees to continuously innovate by providing them incentive beside with state-of-the-art facilities and tools, needed to meet the demands and regulations of today’s rapidly changing biopharmaceutical market.
              </p>
              <p>
                Our work environment is built on “Gender Equality” , collaboration, safety, and operational excellence. From product development to analytical testing and technology transfer, we foster a culture of “proactiveness” that values scientific rigor and open-minded inquiry.
              </p>
            </div>

            {/* Key benefits list */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-teal/10 flex items-center justify-center text-brand-teal mt-0.5">
                  <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-brand-blue text-sm">State-of-the-Art Labs</h4>
                  <p className="text-xs text-slate-500 font-light">Equipped with Moderns Analytical and Formulation Instruments</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-teal/10 flex items-center justify-center text-brand-teal mt-0.5">
                  <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-brand-blue text-sm">Gender Equality</h4>
                  <p className="text-xs text-slate-500 font-light">We believe in equal opportunity.</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-teal/10 flex items-center justify-center text-brand-teal mt-0.5">
                  <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-brand-blue text-sm">Continuous Learning</h4>
                  <p className="text-xs text-slate-500 font-light">Empowering scientist to innovate and lead at higher scale. </p>
                </div>
              </div>
            </div>
          </div>

          {/* Image block */}
          <div className="lg:col-span-6 relative">
            {/* Background design accents */}
            <div className="absolute -inset-4 bg-gradient-to-tr from-brand-teal/10 to-brand-blue/10 rounded-2xl blur-lg transform rotate-1" />
            <div className="relative bg-white p-3 rounded-2xl shadow-xl border border-slate-100/80 overflow-hidden group">
              <img
                src={careersCollaborationImage}
                alt="DelNova Healthcare collaborative scientific laboratory team working together"
                className="w-full h-auto object-cover rounded-xl shadow-inner transition-transform duration-500 group-hover:scale-[1.01]"
              />
              {/* Overlay logo badge */}
              <div className="absolute bottom-6 right-6 bg-white/90 backdrop-blur-md border border-slate-100 rounded-xl px-4 py-2 shadow-lg flex items-center space-x-2">
                <span className="text-xs font-bold text-brand-blue">Innovating Together</span>
                <span className="w-2 h-2 rounded-full bg-brand-teal animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Open Positions Section */}
      <section className="py-20 bg-slate-50 border-t border-b border-slate-100" id="positions">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {/* Section Header */}
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="inline-block text-xs font-semibold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-3 py-1 rounded-full mb-3">
              Join Our Mission
            </div>
            <h2 className="text-3xl font-bold text-brand-blue tracking-tight">
              Current Open Positions
            </h2>
            <p className="text-slate-500 font-light mt-2 text-sm md:text-base">
              Apply to join our R&D and Quality Control teams at Thane. Select a department to filter the listings.
            </p>
          </div>

          {/* Filtering Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {departments.map((dept, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setActiveTab(dept);
                  setExpandedCard("");
                }}
                className={`px-4 py-2 rounded-full text-xs md:text-sm font-medium transition-all duration-200 ${activeTab === dept
                  ? "bg-brand-blue text-white shadow-md shadow-brand-blue/10 scale-102"
                  : "bg-white text-slate-600 hover:text-brand-blue hover:bg-slate-100 border border-slate-200/60"
                  }`}
              >
                {dept === "All" ? "All Departments" : dept}
              </button>
            ))}
          </div>

          {/* Job Openings Grid/List */}
          <div className="space-y-6">
            {filteredJobs.length > 0 ? (
              filteredJobs.map((job, idx) => {
                const isExpanded = expandedCard === `card${job.srno}`;
                return (
                  <div
                    key={idx}
                    className={`bg-white border rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden cursor-pointer ${isExpanded
                      ? "border-brand-teal/40 ring-1 ring-brand-teal/20"
                      : "border-slate-100 hover:border-slate-200"
                      }`}
                    onClick={(e) => handleExpandToggle(`card${job.srno}`, e)}
                  >
                    {/* Header: Collapsed & Permanent Info */}
                    <div className="p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="space-y-2">
                        {/* Department Badge */}
                        <span className="inline-block text-[10px] font-semibold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-2.5 py-0.5 rounded-full">
                          {job.department}
                        </span>
                        {/* Position Title */}
                        <h3 className="text-xl font-bold text-brand-blue tracking-tight">
                          {job.position}
                        </h3>
                        {/* Quick details */}
                        <div className="flex flex-wrap items-center gap-y-1 gap-x-4 text-xs text-slate-500 font-light">
                          <span className="flex items-center">
                            {/* Pin icon */}
                            <svg className="w-3.5 h-3.5 text-brand-teal mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {job.jobLocation}
                          </span>
                          <span className="flex items-center">
                            {/* Briefcase/Graduation icon */}
                            <svg className="w-3.5 h-3.5 text-brand-teal mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                            </svg>
                            {job.job_Requirements.join(" • ")}
                          </span>
                        </div>
                      </div>

                      {/* Expand / See Details Button */}
                      <div>
                        <button
                          onClick={(e) => handleExpandToggle(`card${job.srno}`, e)}
                          className={`w-full md:w-auto inline-flex items-center justify-center space-x-2 text-xs font-semibold px-4 py-2.5 rounded-xl border transition-all ${isExpanded
                            ? "bg-slate-100 text-slate-800 border-slate-200"
                            : "bg-white text-brand-teal hover:text-brand-teal-dark border-slate-200 hover:border-brand-teal/30 hover:bg-slate-50"
                            }`}
                        >
                          <span>{isExpanded ? "Close Details" : "View Details"}</span>
                          <svg
                            className={`w-3.5 h-3.5 transform transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Expandable Section Body */}
                    {isExpanded && (
                      <div className="border-t border-slate-100 bg-slate-50/50 p-6 md:p-8 space-y-6 animate-fadeIn">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          {/* Left Column: Requirements */}
                          <div className="space-y-4">
                            <div className="flex items-center space-x-2">
                              <span className="w-1.5 h-4 bg-brand-teal rounded-full" />
                              <h4 className="text-sm font-bold text-brand-blue uppercase tracking-wider">
                                Technical Requirements
                              </h4>
                            </div>
                            <ul className="space-y-2.5">
                              {job.technicalRequirements.map((req, reqIdx) => (
                                <li key={reqIdx} className="flex items-start text-sm text-slate-600 font-light">
                                  <svg className="w-4 h-4 text-brand-teal mr-2.5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                  </svg>
                                  <span>{req}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Right Column: Job Description */}
                          <div className="space-y-4">
                            <div className="flex items-center space-x-2">
                              <span className="w-1.5 h-4 bg-brand-teal rounded-full" />
                              <h4 className="text-sm font-bold text-brand-blue uppercase tracking-wider">
                                Key Responsibilities
                              </h4>
                            </div>
                            <ol className="space-y-3 list-decimal list-inside text-sm text-slate-600 font-light pl-1">
                              {job.jobDiscription.map((descItem, descIdx) => (
                                <li key={descIdx} className="leading-relaxed pl-1">
                                  <span className="text-slate-600 pl-1">{descItem}</span>
                                </li>
                              ))}
                            </ol>
                          </div>
                        </div>

                        {/* Apply Trigger CTA Row */}
                        <div className="border-t border-slate-200/60 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                          <p className="text-xs text-slate-500 font-light">
                            Please make sure you have your resume / CV ready before starting the application.
                          </p>
                          <Link
                            to="/ApplyHere"
                            state={{ from: job.position }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full sm:w-auto inline-flex items-center justify-center space-x-2 bg-brand-teal hover:bg-brand-teal-dark text-white text-sm font-semibold px-6 py-2.5 rounded-xl shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5"
                          >
                            <span>Apply For This Position</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center bg-white border border-slate-100 rounded-2xl p-12">
                <p className="text-slate-500 font-light">No positions available in this department at the moment.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 4. Spontaneous Applications Card */}
      <section className="py-20 max-w-5xl mx-auto px-6 md:px-12">
        <div className="relative rounded-3xl overflow-hidden bg-gradient-to-r from-brand-blue-deep to-brand-blue text-white p-8 md:p-12 shadow-xl">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-10 mix-blend-overlay"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1920&auto=format&fit=crop')`,
            }}
          />
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="space-y-3 max-w-xl text-left">
              <span className="inline-block text-[10px] font-bold text-brand-teal-light uppercase tracking-widest bg-brand-teal/20 border border-brand-teal/20 px-3 py-1 rounded-full">
                Spontaneous Application
              </span>
              <h3 className="text-2xl md:text-3xl font-bold tracking-tight">
                Don't see a matching open position, or recently graduated?
              </h3>
              <p className="text-slate-300 font-light text-sm md:text-base leading-relaxed">
                Whether you are an experienced pharmaceutical professional or a fresh academic graduate ready to begin your career in formulation science, we want to hear from you. Send us your CV and career goals, and we'll contact you when matching opportunities arise.
              </p>
            </div>
            <div className="w-full md:w-auto flex-shrink-0">
              <Link
                to="/ApplyHere"
                state={{ from: "General Spontaneous Application" }}
                className="w-full md:w-auto inline-flex items-center justify-center space-x-2 bg-brand-teal hover:bg-brand-teal-light text-white text-sm font-bold px-8 py-3.5 rounded-xl shadow-lg transition-all transform hover:-translate-y-0.5"
              >
                <span>Submit Your CV</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
